<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'

import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useQuery } from '@/composables/api'
import { useAccess } from '@/composables/useAccess'

import type { Resource, IdNumber } from '@/types/_general'
import { referenceToResource, isReference } from '@/utils/reference'

import BDialog from '@/components/base/BDialog/BDialog.vue'
import BIdentityNumberField from '@/components/base/BIdentityNumberField/BIdentityNumberField.vue'
import BTooltipBtn from '@/components/base/BTooltipBtn/BTooltipBtn.vue'

import mdiMagnify from '~icons/mdi/magnify'
import mdiOpenInApp from '~icons/mdi/open-in-app'

interface SearchField {
  value: string | null
  loading: boolean
  error: string
}

interface CaseResult {
  _id: string
  reference_number?: string
  creditor_name?: string
}

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const router = useRouter()
const access = useAccess()

const searchFields = reactive<{
  referenceNumber: SearchField
  invoiceNumber: SearchField
  identityNumber: SearchField
  ocrNumber: SearchField
}>({
  referenceNumber: { value: '', loading: false, error: '' },
  invoiceNumber: { value: '', loading: false, error: '' },
  identityNumber: { value: '', loading: false, error: '' },
  ocrNumber: { value: '', loading: false, error: '' },
})

const identityType = ref<IdNumber>()

const showMultipleResultsDialog = ref(false)
const multipleResults = ref<CaseResult[]>([])

function stripNonDigits(field: SearchField) {
  if (field.value != null) {
    const stripped = field.value.replace(/\D/g, '')
    if (stripped !== field.value) field.value = stripped
  }
}

watch(
  () => searchFields.referenceNumber.value,
  () => {
    stripNonDigits(searchFields.referenceNumber)
  }
)
watch(
  () => searchFields.ocrNumber.value,
  () => {
    stripNonDigits(searchFields.ocrNumber)
  }
)

function clearError(field: SearchField) {
  field.error = ''
}

function navigateToResourceItem(resource: Resource, resourceId: string) {
  emit('close')
  void router.push({
    name: 'ItemDisplay',
    params: { resource, resourceId },
  })
}

const referenceNumberInput = computed(() => (searchFields.referenceNumber.value ?? '').trim())

const casesRefQuery = useQuery(
  '/cases',
  computed(() => ({
    query: {
      where: { reference_number: referenceNumberInput.value },
      projection: { _id: 1 },
      max_results: 1,
    },
  })),
  { enabled: false, meta: { notifyOnError: false } }
)

const debtCollectionsRefQuery = useQuery(
  '/case--debt-collections',
  computed(() => ({
    query: {
      where: { reference_number: referenceNumberInput.value },
      projection: { _id: 1 },
      max_results: 1,
    },
  })),
  { enabled: false, meta: { notifyOnError: false } }
)

async function searchReferenceNumber() {
  const field = searchFields.referenceNumber
  const input = referenceNumberInput.value

  if (input === '') {
    field.error = t('common.required')
    return
  }

  field.loading = true
  field.error = ''

  try {
    const canAccessCases = await access.canAccessResourceAsync('cases')
    if (canAccessCases) {
      const result = await casesRefQuery.refetch()
      if (result.data?._items?.[0] != null) {
        navigateToResourceItem('cases', result.data._items[0]._id)
        return
      }
    }

    const canAccessDebtCollections = await access.canAccessResourceAsync('case--debt-collections')
    if (canAccessDebtCollections) {
      const result = await debtCollectionsRefQuery.refetch()
      if (result.data?._items?.[0] != null) {
        navigateToResourceItem('case--debt-collections', result.data._items[0]._id)
        return
      }
    }

    field.error = t('common.not_found')
  } finally {
    field.loading = false
  }
}

const invoiceNumberInput = computed(() => (searchFields.invoiceNumber.value ?? '').trim())

const invoiceQuery = useQuery(
  '/cases',
  computed(() => ({
    query: {
      where: { 'debt.invoice_number': invoiceNumberInput.value },
      projection: { _id: 1, creditor_name: 1, reference_number: 1 },
      max_results: 25,
    },
  })),
  { enabled: false, meta: { notifyOnError: false } }
)

async function searchInvoiceNumber() {
  const field = searchFields.invoiceNumber
  const input = invoiceNumberInput.value

  if (input === '') {
    field.error = t('common.required')
    return
  }

  field.loading = true
  field.error = ''

  try {
    const canAccess = await access.canAccessResourceAsync('cases')
    if (!canAccess) {
      field.error = t('common.not_found')
      return
    }

    const result = await invoiceQuery.refetch()

    if (result.error != null) {
      field.error = t('common.not_found')
      return
    }

    const items = result.data?._items
    if (items == null || items.length === 0) {
      field.error = t('common.not_found')
      return
    }

    if (items.length === 1) {
      const item = items[0]
      if (item != null) {
        navigateToResourceItem('cases', item._id)
      }
    } else {
      multipleResults.value = items
      showMultipleResultsDialog.value = true
    }
  } finally {
    field.loading = false
  }
}

const identityNumberInput = computed(() => (searchFields.identityNumber.value ?? '').trim())

const identityFieldsMap: Partial<Record<IdNumber, string[]>> = {
  swedish_person: ['id_number.swedish_person', 'id_number.swedish_company'],
  swedish_coordination_person: ['id_number.swedish_coordination_person'],
  swedish_company: ['id_number.swedish_company'],
}

const allIdentityFields = [
  'id_number.swedish_company',
  'id_number.swedish_person',
  'id_number.swedish_coordination_person',
  'id_number.other_company',
  'id_number.other_person',
]

const identityNumberOrConditions = computed(() => {
  const fields = (identityType.value && identityFieldsMap[identityType.value]) ?? allIdentityFields
  return fields.map((fieldName) => ({ [fieldName]: identityNumberInput.value }))
})

const debtorsIdentityQuery = useQuery(
  '/debtors',
  computed(() => ({
    query: {
      where: { $or: identityNumberOrConditions.value },
      projection: { _id: 1 },
      max_results: 1,
    },
  })),
  { enabled: false, meta: { notifyOnError: false } }
)

const customersIdentityQuery = useQuery(
  '/customers',
  computed(() => ({
    query: {
      where: { $or: identityNumberOrConditions.value },
      projection: { _id: 1 },
      max_results: 1,
    },
  })),
  { enabled: false, meta: { notifyOnError: false } }
)

async function searchIdentityNumber() {
  const field = searchFields.identityNumber
  const input = identityNumberInput.value

  if (input === '') {
    field.error = t('common.required')
    return
  }

  field.loading = true
  field.error = ''

  try {
    const canAccessDebtors = await access.canAccessResourceAsync('debtors')
    if (canAccessDebtors) {
      const result = await debtorsIdentityQuery.refetch()
      if (result.data?._items?.[0] != null) {
        navigateToResourceItem('debtors', result.data._items[0]._id)
        return
      }
    }

    const canAccessCustomers = await access.canAccessResourceAsync('customers')
    if (canAccessCustomers) {
      const result = await customersIdentityQuery.refetch()
      if (result.data?._items?.[0] != null) {
        navigateToResourceItem('customers', result.data._items[0]._id)
        return
      }
    }

    field.error = t('common.not_found')
  } finally {
    field.loading = false
  }
}

const ocrNumberInput = computed(() => (searchFields.ocrNumber.value ?? '').trim())

const ocrQuery = useQuery(
  '/ocr-numbers',
  computed(() => ({
    query: {
      where: { ocr: ocrNumberInput.value },
      projection: {
        _id: 1,
        case: 1,
        debt_collection: 1,
        enforcement: 1,
        amortization: 1,
        debt_surveillance: 1,
        estate: 1,
      },
      max_results: 1,
    },
  })),
  { enabled: false, meta: { notifyOnError: false } }
)

async function searchOcrNumber() {
  const field = searchFields.ocrNumber
  const input = ocrNumberInput.value

  if (input === '') {
    field.error = t('common.required')
    return
  }

  field.loading = true
  field.error = ''

  try {
    const result = await ocrQuery.refetch()

    if (result.error != null) {
      field.error = t('common.not_found')
      return
    }

    const item = result.data?._items?.[0]
    if (item == null) {
      field.error = t('common.not_found')
      return
    }

    const referenceFields = [
      'case',
      'debt_collection',
      'enforcement',
      'amortization',
      'debt_surveillance',
      'estate',
    ] as const
    for (const ref of referenceFields) {
      const refObj = item[ref]
      if (refObj != null && typeof refObj === 'object' && ref in refObj) {
        const id = (refObj as Record<string, unknown>)[ref]
        if (typeof id === 'string' && isReference(ref)) {
          navigateToResourceItem(referenceToResource(ref), id)
          return
        }
      }
    }

    navigateToResourceItem('ocr-numbers', item._id)
  } finally {
    field.loading = false
  }
}

function handleMultipleResultSelect() {
  showMultipleResultsDialog.value = false
  emit('close')
}

function closeMultipleResultsDialog() {
  showMultipleResultsDialog.value = false
  multipleResults.value = []
}
</script>

<template>
  <section class="search-content d-flex flex-column pa-4 ga-3">
    <v-form @submit.prevent="searchReferenceNumber">
      <v-text-field
        v-model="searchFields.referenceNumber.value"
        :label="t('common.reference_number')"
        :loading="searchFields.referenceNumber.loading"
        :error-messages="searchFields.referenceNumber.error"
        density="compact"
        clearable
        @input="clearError(searchFields.referenceNumber)"
      >
        <template #append>
          <b-tooltip-btn
            :icon="mdiMagnify"
            :loading="searchFields.referenceNumber.loading"
            tooltip="search"
            type="submit"
            density="compact"
            color="primary"
          />
        </template>
      </v-text-field>
    </v-form>

    <v-form @submit.prevent="searchInvoiceNumber">
      <v-text-field
        v-model="searchFields.invoiceNumber.value"
        :label="t('common.invoice_number')"
        :loading="searchFields.invoiceNumber.loading"
        :error-messages="searchFields.invoiceNumber.error"
        clearable
        @input="clearError(searchFields.invoiceNumber)"
      >
        <template #append>
          <b-tooltip-btn
            :icon="mdiMagnify"
            :loading="searchFields.invoiceNumber.loading"
            tooltip="search"
            type="submit"
            density="compact"
            color="primary"
          />
        </template>
      </v-text-field>
    </v-form>

    <v-form @submit.prevent="searchIdentityNumber">
      <b-identity-number-field
        v-model="searchFields.identityNumber.value"
        :label="t('common.social_security_or_organization_number')"
        :loading="searchFields.identityNumber.loading"
        :error-messages="searchFields.identityNumber.error"
        clearable
        @update:model-value="clearError(searchFields.identityNumber)"
        @type="identityType = $event"
      >
        <template #append>
          <b-tooltip-btn
            :icon="mdiMagnify"
            :loading="searchFields.identityNumber.loading"
            tooltip="search"
            type="submit"
            density="compact"
            color="primary"
          />
        </template>
      </b-identity-number-field>
    </v-form>

    <v-form @submit.prevent="searchOcrNumber">
      <v-text-field
        v-model="searchFields.ocrNumber.value"
        :label="t('common.ocr_number')"
        :loading="searchFields.ocrNumber.loading"
        :error-messages="searchFields.ocrNumber.error"
        clearable
        @input="clearError(searchFields.ocrNumber)"
      >
        <template #append>
          <b-tooltip-btn
            :icon="mdiMagnify"
            :loading="searchFields.ocrNumber.loading"
            tooltip="search"
            type="submit"
            density="compact"
            color="primary"
          />
        </template>
      </v-text-field>
    </v-form>

    <b-dialog
      v-model="showMultipleResultsDialog"
      size="xsmall"
      @after-leave="closeMultipleResultsDialog"
    >
      <template #title>
        {{ t('common.more_than_one_result') }}
      </template>

      <v-card-text class="pa-4">
        <div
          v-for="(result, index) in multipleResults"
          :key="result._id"
        >
          <div class="d-flex justify-space-between align-center">
            <div>
              <div class="font-weight-medium">{{ result.reference_number }}</div>
              <div class="text-body-medium text-medium-emphasis">{{ result.creditor_name }}</div>
            </div>
            <b-tooltip-btn
              :icon="mdiOpenInApp"
              tooltip="open"
              :to="{ name: 'ItemDisplay', params: { resource: 'cases', resourceId: result._id } }"
              variant="text"
              @click="handleMultipleResultSelect"
            />
          </div>
          <v-divider
            v-if="index !== multipleResults.length - 1"
            class="my-4"
          />
        </div>
      </v-card-text>
    </b-dialog>
  </section>
</template>

<style scoped>
:deep(.v-input .v-input__append) {
  background: rgb(var(--v-theme-primary));
  border-radius: 4px;
  width: 40px;
  justify-content: center;
}
</style>
